import { useEffect, useState } from "react";
import { Redirect, Route as BaseRoute, Switch, useLocation } from "react-router-dom";
import loadable from "@loadable/component";

import BigFlipLoading from "src/components/BigFlipLoading";
import { isAuthenticated } from "src/helpers/auth";
import privateRoutes from "./private-routes";
import publicRoutes from "./public-routes";
import publicRoutesRouter, { guestRoutes } from "./public-routes-router";
import { RETIRED_ROUTE_REDIRECTS } from "./retired-routes";
import Route from "./Route";
import ScrollToTop from "./ScrollToTop";
import { useNexus } from "src/hooks/useNexus";
import { useReloadUntilNexusReady } from "src/hooks/useNexus/useReloadUntilNexusReady";

const GuestRoutes = loadable(() => import("./GuestLayoutRoutes"));

// True if given pathname is at one of the private routes
const isInPrivateRoute = (pathname) => Object.values(privateRoutes).some(({ path }) => pathname.includes(path));

/**
 * Async load private routes after the application has been loaded.
 * This is to migitate the issue where user needs to wait for the Private Routes js-bundle
 * to be loaded when user is not even using the Private Routes.
 */
const useAsyncPrivateRoute = () => {
  const { pathname } = useLocation();
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    const authenticated = isAuthenticated();
    const routeHasBeenLoaded = routes.length > 0;

    // No need to download private route components if user is not authenticated or if the routes has been downloaded.
    if ((!authenticated && !isInPrivateRoute(pathname)) || routeHasBeenLoaded) {
      return;
    }

    // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/import#dynamic_imports
    import("./private-routes-router").then((module) => {
      setRoutes(module.default);
    });
  }, [pathname]);

  return routes;
};

export default function Routes() {
  const { pathname } = useLocation();
  const { ready } = useNexus();
  useReloadUntilNexusReady(ready);

  const asyncPrivateRoutes = useAsyncPrivateRoute();

  // Can redirect to 404 if user is in public routes, or if user is in private route and all the routes has been loaded
  const canRedirectTo404 = !isInPrivateRoute(pathname) || (isInPrivateRoute(pathname) && asyncPrivateRoutes.length > 0);

  if (!ready) return <BigFlipLoading />;

  return (
    <>
      <ScrollToTop />
      <Switch>
        <BaseRoute exact path={guestRoutes.map((route) => route.path)} component={GuestRoutes} />
        {publicRoutesRouter.map((route, i) => (
          <Route key={i} {...route} />
        ))}
        {/* Direct Switch children (not in a Fragment), so `from` is honoured. Static: works before the private chunk loads. */}
        {RETIRED_ROUTE_REDIRECTS.map(({ from, to }) => (
          <Redirect key={from} from={from} to={to} />
        ))}
        {asyncPrivateRoutes.map((route, i) => (
          <Route key={"p" + i} {...route} isPrivate />
        ))}
        {canRedirectTo404 && (
          // Don't redirect user to anywhere until the `asyncPrivateRoutes` has been loaded
          <>
            <Redirect from="/verification" to={privateRoutes.verificationEmail.path} exact />
            <Redirect from="*" to={publicRoutes.notFound.path} />
          </>
        )}
      </Switch>
    </>
  );
}
